const mono = "'DM Mono', monospace";
const condensed = "'Barlow Condensed', sans-serif";
const sans = "'DM Sans', sans-serif";
const manrope = "'Manrope', sans-serif";
const jakarta = "'Plus Jakarta Sans', sans-serif";
const plex = "'IBM Plex Mono', monospace";

export const APPEARANCE_KEY = 'tradelog-appearance-v1';
export const THEMES = {
  classic: {
    name: 'Classic', description: 'The original TradeLog. Pure black, electric green, unmistakably yours.',
    typography: 'Barlow Condensed + DM Mono', font: mono, heading: condensed, numeric: mono,
    bg: '#080909', s1: '#101212', s2: '#171a19', s3: '#292e2b', s4: '#3b4540',
    t1: '#f4f6f4', t2: '#c7cdc9', t3: '#a4ada7', t4: '#8e9b92', t5: '#819187',
    accent: '#4ade80', positive: '#4ade80', negative: '#f87171', neutral: '#e8c95c', gold: '#c9a84c',
    nav: '#080a09', radius: '10px', shadow: '0 2px 8px #00000020', light: false, card: 'dark',
  },
  studio: {
    name: 'Studio', description: 'Quiet graphite, considered spacing, and a crisp editorial rhythm.',
    typography: 'Manrope + DM Sans', font: sans, heading: manrope, numeric: sans,
    bg: '#111313', s1: '#191c1c', s2: '#222626', s3: '#333938', s4: '#4a5350',
    t1: '#f2f5f3', t2: '#cad2ce', t3: '#a7b2ac', t4: '#96a39c', t5: '#8b9a92',
    accent: '#76dfb0', positive: '#76dfb0', negative: '#f58a94', neutral: '#e5c779', gold: '#d4b677',
    nav: '#151818', radius: '16px', shadow: '0 8px 24px #00000018', light: false, card: 'studio',
  },
  midnight: {
    name: 'Midnight', description: 'Deep navy, cool blue controls, and precise instrument-style numbers.',
    typography: 'Plus Jakarta Sans + IBM Plex Mono', font: jakarta, heading: jakarta, numeric: plex,
    bg: '#0b1020', s1: '#121b2e', s2: '#19243b', s3: '#2b3953', s4: '#415574',
    t1: '#eff4ff', t2: '#c9d5e9', t3: '#a7b7d0', t4: '#94a7c3', t5: '#859ab9',
    accent: '#91b4ff', positive: '#62e3ae', negative: '#ff929e', neutral: '#f0d380', gold: '#ddc28a',
    nav: '#0c1425', radius: '12px', shadow: '0 8px 28px #03091944', light: false, card: 'midnight',
  },
  paper: {
    name: 'Paper', description: 'Clean white surfaces, deep ink, and balanced contrast for brighter days.',
    typography: 'Manrope + DM Sans', font: sans, heading: manrope, numeric: sans,
    bg: '#f0f3f1', s1: '#ffffff', s2: '#f1f5f2', s3: '#dce4df', s4: '#b8c9bf',
    t1: '#192a20', t2: '#334c3e', t3: '#526b5c', t4: '#5d7566', t5: '#61796a',
    accent: '#176b46', positive: '#187b4b', negative: '#b82d43', neutral: '#886619', gold: '#927128',
    nav: '#f9fbf9', radius: '12px', shadow: '0 3px 14px #254a3210', light: true, card: 'light',
  },
};

export const DEFAULT_APPEARANCE = { theme: 'classic', layout: 'classic', density: 'comfortable', quiet: false, phoneTilt: true };
export function normalizeAppearance(value = {}) {
  value = value && typeof value === "object" ? value : {};
  return {
    theme: Object.prototype.hasOwnProperty.call(THEMES,value.theme) ? value.theme : 'classic',
    layout: ['classic', 'focus'].includes(value.layout) ? value.layout : 'classic',
    density: value.density === 'compact' ? 'compact' : 'comfortable',
    quiet: value.quiet === true,
    phoneTilt: value.phoneTilt !== false,
  };
}
export function readAppearance() {
  try {
    const saved = JSON.parse(localStorage.getItem(APPEARANCE_KEY) || 'null');
    if (saved && typeof saved === 'object') return normalizeAppearance(saved);
    return { ...DEFAULT_APPEARANCE, theme: localStorage.getItem('tradelog-theme') === 'light' ? 'paper' : 'classic' };
  } catch { return { ...DEFAULT_APPEARANCE }; }
}

// Legacy components compose hex colours with alpha suffixes. Keep their palette
// in sync with the provider while the newer components consume the same CSS tokens.
let activeTheme = THEMES[readAppearance().theme];
export function activateTheme(id) { activeTheme = THEMES[id] || THEMES.classic; }
export const colors = {
  get win() { return activeTheme.positive; }, get loss() { return activeTheme.negative; },
  get be() { return activeTheme.neutral; }, get gold() { return activeTheme.gold; },
  get goldL() { return activeTheme.gold; }, get blue() { return activeTheme.light ? '#3269ad' : '#83acff'; },
  bg: 'var(--c-bg)', s1: 'var(--c-s1)', s2: 'var(--c-s2)', s3: 'var(--c-s3)', s4: 'var(--c-s4)',
  t1: 'var(--c-t1)', t2: 'var(--c-t2)', t3: 'var(--c-t3)', t4: 'var(--c-t4)', t5: 'var(--c-t5)',
  get amber() { return activeTheme.neutral; },
};

export function themeVariables(theme) {
  const variables = {};
  for (const key of ['bg','s1','s2','s3','s4','t1','t2','t3','t4','t5']) variables[`--c-${key}`] = theme[key];
  return { ...variables,
    '--tl-accent': theme.accent, '--tl-positive': theme.positive, '--tl-loss': theme.negative,
    '--tl-neutral': theme.neutral, '--tl-gold': theme.gold, '--tl-font': theme.font,
    '--tl-heading': theme.heading, '--tl-numeric': theme.numeric, '--tl-radius': theme.radius,
    '--tl-shadow': theme.shadow, '--nav-bg': theme.nav, '--placeholder': theme.t4,
    '--color-scheme': theme.light ? 'light' : 'dark',
    '--scrollbar-track':theme.bg, '--scrollbar-thumb':theme.s4,
    '--tl-button-text': theme.light ? '#ffffff' : '#081810',
    '--shadow': theme.shadow, '--shadow-lg': theme.light ? '0 16px 60px #233d2420' : '0 20px 70px #00000066',
    '--chart-axis': theme.t4, '--chart-grid': theme.s3, '--chart-zero': theme.s4, '--chart-cross': theme.t5,
  };
}
